import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from "react";
import BackendAxios from "../BackendAxios";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshTokenValue, setRefreshTokenValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshRequestRef = useRef(null);

  // Function to clear auth data
  const clearAuth = useCallback(() => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authRefreshToken");
    localStorage.removeItem("authUser");
    setToken(null);
    setRefreshTokenValue(null);
    setUser(null);
    delete BackendAxios.defaults.headers.common["Authorization"];
  }, []);

  const setAuthState = useCallback(({ accessToken, refreshToken, userData }) => {
    localStorage.setItem("authToken", accessToken);
    localStorage.setItem("authRefreshToken", refreshToken);
    localStorage.setItem("authUser", JSON.stringify(userData));

    setToken(accessToken);
    setRefreshTokenValue(refreshToken);
    setUser(userData);
    BackendAxios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  }, []);

  const refreshAccessToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem("authRefreshToken");

    if (!storedRefreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await BackendAxios.post("/auth/refresh", {
      refreshToken: storedRefreshToken,
    });

    const { token: newToken, refreshToken: newRefreshToken, user: userData } = response.data;
    setAuthState({
      accessToken: newToken,
      refreshToken: newRefreshToken,
      userData,
    });

    return newToken;
  }, [setAuthState]);

  const ensureFreshAccessToken = useCallback(async () => {
    if (!refreshRequestRef.current) {
      refreshRequestRef.current = refreshAccessToken().finally(() => {
        refreshRequestRef.current = null;
      });
    }

    return refreshRequestRef.current;
  }, [refreshAccessToken]);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("authToken");
    const storedRefreshToken = localStorage.getItem("authRefreshToken");
    const storedUser = localStorage.getItem("authUser");

    if (storedToken && storedRefreshToken && storedUser) {
      setToken(storedToken);
      setRefreshTokenValue(storedRefreshToken);
      setUser(JSON.parse(storedUser));
      BackendAxios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    } else {
      clearAuth();
    }
    setLoading(false);
  }, [clearAuth]);

  // Add axios interceptor to refresh and retry once when access token expires.
  useEffect(() => {
    const interceptor = BackendAxios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        if (!originalRequest || status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        const requestPath = originalRequest.url || "";
        const isAuthEndpoint =
          requestPath.includes("/auth/login") ||
          requestPath.includes("/auth/register") ||
          requestPath.includes("/auth/refresh");

        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        try {
          originalRequest._retry = true;
          const newAccessToken = await ensureFreshAccessToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return BackendAxios(originalRequest);
        } catch (refreshError) {
          clearAuth();
          return Promise.reject(refreshError);
        }
      }
    );

    return () => {
      BackendAxios.interceptors.response.eject(interceptor);
    };
  }, [clearAuth, ensureFreshAccessToken]);

  const login = async (email, password) => {
    try {
      const response = await BackendAxios.post("/auth/login", {
        email,
        password,
      });

      const {
        token: newToken,
        refreshToken: newRefreshToken,
        user: userData,
      } = response.data;

      setAuthState({
        accessToken: newToken,
        refreshToken: newRefreshToken,
        userData,
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Login failed. Please try again.",
      };
    }
  };

  const register = async (username, email, password, displayName) => {
    try {
      const response = await BackendAxios.post("/auth/register", {
        username,
        email,
        password,
        displayName,
      });

      const {
        token: newToken,
        refreshToken: newRefreshToken,
        user: userData,
      } = response.data;

      setAuthState({
        accessToken: newToken,
        refreshToken: newRefreshToken,
        userData,
      });

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Registration failed. Please try again.",
      };
    }
  };

  const logout = () => {
    clearAuth();
  };

  const value = {
    user,
    token,
    refreshToken: refreshTokenValue,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

